import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

export interface DropdownMenuStyle {
  position: 'fixed';
  top: number;
  left: number;
  transform: string;
}

export interface UseDropdownMenu {
  /** Whether the menu is currently open. */
  open: boolean;
  /** Attach to the trigger button — used for positioning and outside-click detection. */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** Attach to the portaled menu element. */
  menuRef: React.RefObject<HTMLDivElement | null>;
  /** Inline style for the menu (fixed position); `undefined` while closed. */
  menuStyle: DropdownMenuStyle | undefined;
  /** Open the menu anchored to the given trigger element (pass `event.currentTarget`). */
  openMenu: (trigger: HTMLButtonElement) => void;
  /** Close the menu. */
  closeMenu: () => void;
}

/**
 * Headless open/close + positioning mechanics for a single row-actions dropdown menu (a
 * cog/trigger button plus a menu that must stay on-screen).
 *
 * The consuming component renders the menu through a portal into `<body>` with `menuStyle`
 * applied, so it can never be clipped by the table / mobile-card containers that hold the
 * trigger — this is what keeps the menu on-screen in the mobile card view (issue #325).
 * Positioning:
 *   - `menuStyle` fixes the menu just below the trigger, right-anchored (translateX(-100%)),
 *     so it opens leftward into the row.
 *   - a layout effect nudges it back on-screen if that would push it off the left edge
 *     (narrow / mobile viewports) or below the fold.
 *
 * Mirrors the technique the Managed WordPress / Manage Packages tables use inline
 * (PackageTablePage & ManagePackagesPage each carry their own copy); extracted here so
 * newer pages can share one implementation.
 *
 * Usage:
 *   const { open, triggerRef, menuRef, menuStyle, openMenu, closeMenu } = useDropdownMenu();
 *   <button ref={triggerRef} aria-expanded={open}
 *           onClick={(e) => (open ? closeMenu() : openMenu(e.currentTarget))}>…</button>
 *   {open && menuStyle &&
 *     createPortal(<div ref={menuRef} role="menu" style={menuStyle}>…</div>, document.body)}
 */
export function useDropdownMenu(): UseDropdownMenu {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openMenu = useCallback((trigger: HTMLButtonElement) => {
    const rect = trigger.getBoundingClientRect();
    // Anchor the menu's right edge to the trigger's right edge, dropping below it.
    setPosition({ top: rect.bottom + 4, left: rect.right });
    setOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setPosition(null);
  }, []);

  const menuStyle = useMemo<DropdownMenuStyle | undefined>(
    () =>
      position
        ? {
            position: 'fixed',
            top: position.top,
            left: position.left,
            transform: 'translateX(-100%)',
          }
        : undefined,
    [position]
  );

  // Keep the menu on-screen. It is right-anchored (translateX(-100%)), so on a narrow viewport
  // its left edge can go negative — re-anchor to the trigger's left edge (clamped into view) and
  // flip above the trigger if it would drop below the fold.
  /* v8 ignore start -- viewport overflow correction: getBoundingClientRect is 0 in JSDOM */
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !triggerRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    if (menuRect.left < 0) {
      let left = triggerRect.left;
      if (left + menuRect.width > window.innerWidth) {
        left = Math.max(4, window.innerWidth - menuRect.width - 4);
      }
      menuRef.current.style.transform = 'none';
      menuRef.current.style.left = `${left}px`;
    }
    if (menuRect.bottom > window.innerHeight) {
      menuRef.current.style.top = `${triggerRect.top - menuRect.height - 4}px`;
    }
  });
  /* v8 ignore stop */

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      closeMenu();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, closeMenu]);

  return { open, triggerRef, menuRef, menuStyle, openMenu, closeMenu };
}
